import type { MasterAdminNotificationRow } from '../services/MasterAdmin/masterAdminNotificationService';

export function getNotificationLink(n: MasterAdminNotificationRow): string | null {
  const d = n.data || {};
  const bookingId = typeof d.bookingId === 'string' ? d.bookingId : null;
  const vendorId = typeof d.vendorId === 'string' ? d.vendorId : null;
  const orderId = typeof d.orderId === 'string' ? d.orderId : null;
  const navigation =
    d.navigation && typeof d.navigation === 'object'
      ? (d.navigation as Record<string, unknown>)
      : null;
  const profileType =
    typeof navigation?.profileType === 'string'
      ? navigation.profileType
      : (typeof d.vendorType === 'string' ? d.vendorType : '');

  if (n.type === 'CLINIC_PROFILE_UPDATED') {
    return '/vendor-profile-updates';
  }

  if (n.type === 'VENDOR_CONTACT_QUERY_CREATED') {
    return '/operations/vendor-contact-queries';
  }

  if (n.type === 'DOCTOR_BLOG_SUBMITTED' || n.type === 'DOCTOR_BLOG_UPDATED') {
    const writerPath =
      typeof navigation?.writerWebPath === 'string' && navigation.writerWebPath.trim()
        ? navigation.writerWebPath.trim()
        : '/writer/doctor-blogs';
    const masterPath =
      typeof navigation?.adminWebPath === 'string' && navigation.adminWebPath.trim()
        ? navigation.adminWebPath.trim()
        : vendorId
          ? `/vendors/doctor-clinic/${vendorId}`
          : '/notifications';
    try {
      const raw = localStorage.getItem('user');
      const role = raw ? String(JSON.parse(raw)?.role || '').toUpperCase() : '';
      if (role === 'WRITER') return writerPath;
    } catch {
      /* ignore */
    }
    return masterPath;
  }

  if (n.type === 'AMBULANCE_BOOKING_REQUESTED') {
    const params = new URLSearchParams({ tab: 'ambulance' });
    if (bookingId) params.set('bookingId', bookingId);
    return `/request-queue?${params.toString()}`;
  }

  if (n.type === 'INSTANT_CONSULTATION_REQUESTED') {
    const appointmentId =
      typeof d.appointmentId === 'string'
        ? d.appointmentId
        : typeof d.clinicAppointmentId === 'string'
          ? d.clinicAppointmentId
          : null;
    const params = new URLSearchParams({ tab: 'instant-consultation' });
    if (appointmentId) params.set('appointmentId', appointmentId);
    return `/request-queue?${params.toString()}`;
  }

  if (n.type === 'CLINIC_APPOINTMENT_BOOKED' && vendorId) {
    return `/vendors/doctor-clinic/${vendorId}`;
  }

  if (bookingId && vendorId && n.type !== 'AMBULANCE_BOOKING_REQUESTED') {
    return `/vendors/ambulance/${vendorId}/booking/${bookingId}`;
  }
  if (orderId && vendorId) {
    return `/vendors/medical-store/${vendorId}/order/${orderId}`;
  }

  const prescriptionId = typeof d.prescriptionId === 'string' ? d.prescriptionId : null;
  if (prescriptionId) {
    return `/request-queue?prescriptionId=${encodeURIComponent(prescriptionId)}`;
  }

  if (vendorId && n.type === 'VENDOR_REGISTERED') {
    const key = String(profileType || '').toLowerCase();
    if (key.includes('ambulance')) return `/vendors/ambulance/${vendorId}`;
    if (key.includes('hospital')) return `/vendors/hospital/${vendorId}`;
    if (key.includes('medicine') || key.includes('medical')) return `/vendors/medical-store/${vendorId}`;
    if (key.includes('doctor') || key.includes('clinic')) return `/vendors/doctor-clinic/${vendorId}`;
    if (key.includes('blood')) return `/vendors/blood-bank/${vendorId}`;
    if (key.includes('lab') || key.includes('diagnostic')) return `/vendors/diagnostic-center/${vendorId}`;
    if (key.includes('product')) return `/vendors/product/${vendorId}`;
    return `/vendors`;
  }

  if (n.type === 'GRIEVANCE_RAISED') {
    const grievanceId = typeof d.grievanceId === 'string' ? d.grievanceId : null;
    if (grievanceId) {
      return `/grievances/${encodeURIComponent(grievanceId)}/resolve`;
    }
    return '/grievances';
  }

  const adminWebPath = typeof navigation?.adminWebPath === 'string' ? navigation.adminWebPath : null;
  if (adminWebPath) {
    if (adminWebPath.startsWith('/master-admin/vendors/')) {
      return adminWebPath.replace('/master-admin/vendors/', '/vendors/');
    }
    if (adminWebPath.startsWith('/master-admin/')) {
      return adminWebPath.replace('/master-admin', '') || '/';
    }
    return adminWebPath;
  }
  return null;
}

export function formatNotificationBody(n: MasterAdminNotificationRow): string {
  const d = n.data || {};
  const userName = typeof d.userName === 'string' && d.userName.trim() ? d.userName.trim() : 'A user';
  const vendorName =
    typeof d.vendorName === 'string' && d.vendorName.trim() ? d.vendorName.trim() : 'a vendor';
  const vendorsNotified = typeof d.vendorsNotified === 'number' ? d.vendorsNotified : null;

  if (n.type === 'MEDICINE_PRESCRIPTION_REQUESTED') {
    return `${userName} submitted a new medicine request${
      vendorsNotified != null ? ` to ${vendorsNotified} vendor(s)` : ''
    }.`;
  }
  if (n.type === 'MEDICINE_ORDER_CREATED') {
    return `Medicine order created for ${userName} with ${vendorName}.`;
  }
  if (n.type === 'MEDICINE_PRESCRIPTION_CANCELLED') {
    return `${userName} cancelled a medicine prescription request.`;
  }
  if (n.type === 'AMBULANCE_BOOKING_REQUESTED') {
    return `${userName} requested a new ambulance booking with ${vendorName}.`;
  }
  if (n.type === 'AMBULANCE_PAYMENT_DONE') {
    return `Payment completed for an ambulance booking by ${userName}.`;
  }
  if (n.type === 'AMBULANCE_HELP_REQUESTED') {
    return `${userName} submitted a help request for ${vendorName}.`;
  }
  if (n.type === 'AMBULANCE_BOOKING_CANCELLED') {
    return `${userName} cancelled an ambulance booking with ${vendorName}.`;
  }
  if (n.type === 'CLINIC_PROFILE_UPDATED') {
    const changed =
      Array.isArray(d.changedFields) && d.changedFields.length
        ? (d.changedFields as string[]).join(', ')
        : null;
    const vendorLabel =
      typeof d.vendorName === 'string' && d.vendorName.trim() ? d.vendorName.trim() : 'A vendor';
    return changed
      ? `${vendorLabel} updated their profile. Updated: ${changed}.`
      : `${vendorLabel} updated their profile.`;
  }
  if (n.type === 'CLINIC_APPOINTMENT_BOOKED') {
    const doctorName =
      typeof d.doctorName === 'string' && d.doctorName.trim() ? d.doctorName.trim() : 'a doctor';
    const consultationType =
      typeof d.consultationType === 'string' && d.consultationType.trim()
        ? d.consultationType.trim()
        : 'consultation';
    const appointmentVendorId = typeof d.vendorId === 'string' ? d.vendorId : null;
    return appointmentVendorId
      ? `${userName} booked a ${consultationType} appointment with ${doctorName}.`
      : `${userName} requested a ${consultationType} consultation.`;
  }
  if (n.type === 'INSTANT_CONSULTATION_REQUESTED') {
    return `${userName} is requesting an instant consultation.`;
  }
  if (n.type === 'GRIEVANCE_RAISED') {
    const gNum =
      typeof d.grievanceNumber === 'string' && d.grievanceNumber.trim()
        ? d.grievanceNumber.trim()
        : null;
    return gNum
      ? `${userName} submitted grievance ${gNum}.`
      : `${userName} submitted a new grievance.`;
  }
  if (n.type === 'VENDOR_CONTACT_QUERY_CREATED') {
    const namedVendor =
      typeof d.vendorName === 'string' && d.vendorName.trim() ? d.vendorName.trim() : null;
    if (!namedVendor) {
      return String(n.body || '').trim() || 'A vendor submitted a contact query.';
    }
    const subject =
      typeof d.querySubject === 'string' && d.querySubject.trim()
        ? d.querySubject.trim()
        : null;
    return subject
      ? `${namedVendor} submitted: ${subject}`
      : `${namedVendor} submitted a contact query.`;
  }
  if (n.type === 'DOCTOR_BLOG_SUBMITTED' || n.type === 'DOCTOR_BLOG_UPDATED') {
    const doctorName =
      typeof d.doctorName === 'string' && d.doctorName.trim() ? d.doctorName.trim() : 'A doctor';
    const blogTitle =
      typeof d.blogTitle === 'string' && d.blogTitle.trim() ? d.blogTitle.trim() : 'a blog';
    return n.type === 'DOCTOR_BLOG_UPDATED'
      ? `${doctorName} updated "${blogTitle}" and resubmitted it for review.`
      : `${doctorName} submitted "${blogTitle}" for review.`;
  }

  return String(n.body || '')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, '')
    .replace(/\b[A-Za-z0-9_-]{20,}\b/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function getNotificationServiceLabel(type: string): string {
  const key = type.toUpperCase();
  if (key.includes('AMBULANCE')) return 'Ambulance';
  if (key.includes('MEDICINE') || key.includes('PRESCRIPTION')) return 'Medicine';
  if (key.includes('GRIEVANCE')) return 'Grievance';
  if (key.includes('VENDOR')) return 'Vendor';
  if (key.includes('BLOOD')) return 'Blood bank';
  if (key.includes('HOSPITAL')) return 'Hospital';
  if (key.includes('LAB') || key.includes('DIAGNOSTIC')) return 'Diagnostics';
  if (key.includes('INSTANT_CONSULTATION')) return 'Instant consultation';
  if (key.includes('DOCTOR_BLOG') || key.includes('BLOG')) return 'Doctor blogs';
  if (key.includes('DOCTOR') || key.includes('CLINIC')) return 'Doctor clinic';
  if (key.includes('ORDER') || key.includes('PAYMENT')) return 'Orders and payments';
  return 'Other';
}
